// src/views/StoreAnalytics.js
import React, { useState, useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CWidgetStatsA,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CFormSelect,
  CSpinner,
  CFormLabel,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CModalTitle,
  CButton,
  CFormInput,
} from "@coreui/react";
import { CChartBar, CChartDoughnut } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cibEventStore, cilChartPie, cilMap, cilCalendar } from "@coreui/icons";
import { getSummary, getGeographicDistribution, getSalesPerformance, getMapDistribution, getVisibilityTrends, getStockPerStoreMonthly } from "src/services/Analytics";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import StockLatest from "src/components/StockLatest";
import DataTable from "src/components/DataTable";
import { Box } from "@mui/material";

// Fix for default marker icons (required for React-Leaflet)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});
const StoreAnalytics = () => {
  const [summary, setSummary] = useState([]);
  // const [visitTrends, setVisitTrends] = useState([]);
  const [visibilityTrends, setVisibilityTrends] = useState([]);
  const [mapDistribution, setMapDistribution] = useState([]);
  const [geoDistribution, setGeoDistribution] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [timeRange, setTimeRange] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [stockMonthly, setStockMonthly] = useState(null);
  
  // Add modal state
  const [showStoresModal, setShowStoresModal] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Add pagination state for the modal DataTable
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Remove pagination and group selection states
  // const [stockRatioGroup, setStockRatioGroup] = useState("below_50");
  // const [datasetPage, setDatasetPage] = useState(0);
  // const datasetsPerPage = 10;

  const navigate = useNavigate();

  const center = [-6.91, 107.61]; // Latitude, Longitude of Bandung

  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  const handleBarClick = (index) => {
    if (index == null) return;
    if (index < geoDistribution.length) {
      const id = geoDistribution[index].id;
      navigate(`/stores?provinceId=${id}`)
    }
  };

  const fetchData = React.useCallback(async () => {
    const filters = getFilterParams();
    setLoading(true);
    try {
      const [resx, maps, geo, sales] = await Promise.all([
        getSummary(filters),
        getMapDistribution(filters),
        getGeographicDistribution(filters), // Sekarang menerima parameter smdId, provinceId, cityId
        getSalesPerformance(filters), // Sekarang menerima parameter smdId, provinceId, cityId
      ]);
      setSummary(resx);
      setMapDistribution(maps.stores);
      setGeoDistribution(geo.data);
      setSalesPerformance(sales.data);
      
      // visibility trend fetch should not block page
      try {
        const vis = await getVisibilityTrends(timeRange, filters);
        setVisibilityTrends(vis?.data || vis || []);
      } catch (e) {
        setVisibilityTrends([]);
      }
      
      // stock per store monthly should not block page
      try {
        const monthly = await getStockPerStoreMonthly({ ...filters, months: 6 });
        setStockMonthly(monthly?.data || monthly || null);
      } catch (e) {
        setStockMonthly([]);
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function buildVisibilityTrendDataset(data) {
    // Get all unique periods (month, quarter, etc.)
    const labels = [...new Set(data.map(item => item.label))];

    // Get all unique visibility levels
    const visibilities = [...new Set(data.map(item => item.visibility))];

    // Define a color palette (expand as needed)
    const colorPalette = [
      "#FF6384", // red
      "#FFCE56", // yellow
      "#00A86B", // green
      "#36A2EB", // blue
      "#4BC0C0", // teal
      "#9966FF", // purple
      "#FF9F40", // orange
      "#C71585", // violet
    ];

    // Build datasets for each visibility
    const datasets = visibilities.map((visibility, idx) => ({
      label: visibility,
      data: labels.map(label => {
        const found = data.find(item =>
          (item.month === label || item.quarter === label || item.yearweek === label || item.biweek === label) &&
          item.visibility === visibility
        );
        return found ? found.visit_count : 0;
      }),
      backgroundColor: colorPalette[idx % colorPalette.length],
      borderColor: colorPalette[idx % colorPalette.length],
      borderWidth: 1,
    }));

    return { labels, datasets };
  }

  const buildMonthlyChart = (raw) => {
    if (!raw || !raw.chartData || !Array.isArray(raw.chartData) || !raw.chartData.length) {
      return { labels: [], datasets: [] };
    }
    
    const chartData = raw.chartData;
    const categories = raw.categories || [];
    
    // Extract months from chartData
    const months = chartData.map(item => item.month);
    
    // Build datasets for each category
    const datasets = categories.map(category => ({
      label: category.name,
      data: chartData.map(item => {
        const categoryData = item[category.key];
        // Handle new structure where category data has count property
        return categoryData && typeof categoryData === 'object' ? categoryData.count : (categoryData || 0);
      }),
      backgroundColor: category.color,
      borderColor: category.color,
      borderWidth: 1,
    }));
    
    return { labels: months, datasets };
  };

  const chart = buildMonthlyChart(stockMonthly);

  // Remove pagination since we now have fixed categories
  // const totalPages = Math.ceil(chart.datasets.length / datasetsPerPage);
  // const paginatedDatasets = chart.datasets.slice(
  //   datasetPage * datasetsPerPage,
  //   (datasetPage + 1) * datasetsPerPage
  // );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  // Add click handlers for doughnut charts
  const handleTypeDistributionClick = (event, elements) => {
    if (elements.length > 0) {
      const dataIndex = elements[0].index;
      const label = summary.data.distribution[dataIndex]?.type;
      const smdId = ls.get("smd")?.id && ls.get("smd")?.id !== "999" && ls.get("smd")?.value !== "all" ? ls.get("smd")?.id : null;
      navigate(`/stores?type=${label}${smdId ? `&smdId=${smdId}` : ''}`) 
      // You can add additional logic here like navigation or showing details
    }
  };

  const handleVisibilityDistributionClick = (event, elements) => {
    if (elements.length > 0) {
      const dataIndex = elements[0].index;
      const label = summary.data.visibility[dataIndex]?.visibility;
      const smdId = ls.get("smd")?.id && ls.get("smd")?.id !== "999" && ls.get("smd")?.value !== "all" ? ls.get("smd")?.id : null;
      navigate(`/stores?visibility=${label}${smdId ? `&smdId=${smdId}` : ''}`) 
      // You can add additional logic here like navigation or showing details
    }
  };

  const handleProductDistributionClick = (event, elements) => {
    if (elements.length > 0) {
      const dataIndex = elements[0].index;
      const label = summary.data.product[dataIndex]?.product_group;
      const smdId = ls.get("smd")?.id && ls.get("smd")?.id !== "999" && ls.get("smd")?.value !== "all" ? ls.get("smd")?.id : null;
      navigate(`/stores?product_group=${label}${smdId ? `&smdId=${smdId}` : ''}`) 
      // You can add additional logic here like navigation or showing details
    }
  };

  // Update the handleBarChartClick function
  const handleBarChartClick = (event, elements) => {
    if (elements.length > 0) {
      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const dataIndex = element.index;
      
      // Get the month and category from the clicked bar
      const month = chart.labels[dataIndex];
      const category = chart.datasets[datasetIndex]?.label;
      
      // Store the selected month and category
      setSelectedMonth(month);
      setSelectedCategory(category);
      
      // Load stores for the selected category
      loadStoresForCategory(month, category);
      
      setShowStoresModal(true);
      // Reset pagination when opening modal
      setPaginationModel({ page: 0, pageSize: 10 });
    }
  };

  // Function to load stores for a specific category
  const loadStoresForCategory = (month, category) => {
    const monthData = stockMonthly?.chartData?.find(item => item.month === month);
    if (monthData) {
      // Map category labels to keys
      const categoryKeyMap = {
        'Below 50%': 'below_50',
        '50-75%': 'between_50_75', 
        'Above 75%': 'above_75'
      };
      
      const actualCategoryKey = categoryKeyMap[category];
      const categoryData = monthData[actualCategoryKey];
      
      if (categoryData && categoryData.stores) {
        setSelectedStores(categoryData.stores);
        setModalTitle(`${category} - ${month} (${categoryData.count} stores)`);
      } else {
        setSelectedStores([]);
        setModalTitle(`${category} - ${month} (0 stores)`);
      }
    }
  };

  // Handle category change
  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
    loadStoresForCategory(selectedMonth, newCategory);
    // Reset pagination when changing category
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  // Handle pagination change
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  // Handle store row click - navigate to store details
  const handleStoreRowClick = (params) => {
    const storeId = params.row.id;
    if (storeId) {
      // Close the modal first
      setShowStoresModal(false);
      // Navigate to store details
      navigate(`/stores/details/${storeId}`);
    }
  };

  // Prepare DataTable rows and columns
  const storeRows = selectedStores.map((store, index) => ({
    id: store.id || index,
    name: store.name,
    products_with_stock: store.products_with_stock,
    ratio: store.ratio,
  }));

  const storeColumns = [
    { 
      field: "name", 
      headerName: "Store Name", 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{ 
            maxWidth: "100%", 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            whiteSpace: "nowrap",
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main'
            }
          }}
          title={`Click to view details: ${String(params.value ?? "")}`}
        >
          {String(params.value ?? "-")}
        </Box>
      )
    },
    { 
      field: "products_with_stock", 
      headerName: "Products with Stock", 
      width: 180,
      type: 'number'
    },
    { 
      field: "ratio", 
      headerName: "Stock Ratio", 
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <Box
            sx={{
              backgroundColor: params.value < 50 ? '#ff6b6b' : 
                              params.value < 75 ? '#ffd93d' : '#6bcf7f',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              minWidth: '60px',
              textAlign: 'center'
            }}
          >
            {params.value?.toFixed(2)}%
          </Box>
        </Box>
      )
    },
  ];

  const convertTimePeriodToDateRange = (label, timeRange) => {
    console.log('Converting time period:', { label, timeRange }); // Debug log
    
    let dateFrom, dateTo;

    switch (timeRange) {
      case 'weekly':
        // For weekly periods
        if (label.includes('W')) {
          const [year, week] = label.split('-W');
          const weekNum = parseInt(week);
          
          if (isNaN(weekNum) || isNaN(parseInt(year))) {
            console.error('Invalid week format:', label);
            // Fallback to current week
            const now = new Date();
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
            dateFrom = new Date(startOfWeek);
            dateTo = new Date(startOfWeek);
            dateTo.setDate(startOfWeek.getDate() + 6);
          } else {
            const jan1 = new Date(year, 0, 1);
            const jan1DayOfWeek = jan1.getDay();
            
            const firstMonday = new Date(jan1);
            const daysToFirstMonday = jan1DayOfWeek === 0 ? 1 : 8 - jan1DayOfWeek;
            firstMonday.setDate(jan1.getDate() + daysToFirstMonday);
            
            dateFrom = new Date(firstMonday);
            dateFrom.setDate(firstMonday.getDate() + (weekNum - 1) * 7);
            
            dateTo = new Date(dateFrom);
            dateTo.setDate(dateFrom.getDate() + 6);
          }
        } else {
          // Try to parse as date
          const baseDate = new Date(label);
          if (isNaN(baseDate.getTime())) {
            console.error('Invalid date format:', label);
            // Fallback to current week
            const now = new Date();
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay() + 1);
            dateFrom = new Date(startOfWeek);
            dateTo = new Date(startOfWeek);
            dateTo.setDate(startOfWeek.getDate() + 6);
          } else {
            dateFrom = new Date(baseDate);
            dateTo = new Date(baseDate);
            dateTo.setDate(baseDate.getDate() + 6);
          }
        }
        break;
      
      case 'biweekly':
        // For biweekly periods - handle different possible formats
        console.log('Processing biweekly label:', label);
        
        if (label.includes('W')) {
          // Format like "2024-W01" or "2024-W1"
          const [year, week] = label.split('-W');
          const weekNum = parseInt(week);
          
          console.log('Parsed biweekly:', { year, week, weekNum });
          
          if (isNaN(weekNum) || isNaN(parseInt(year))) {
            console.error('Invalid biweekly format:', label);
            // Fallback to current biweekly period
            const now = new Date();
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
            dateFrom = new Date(startOfWeek);
            dateTo = new Date(startOfWeek);
            dateTo.setDate(startOfWeek.getDate() + 13);
          } else {
            const jan1 = new Date(year, 0, 1);
            const jan1DayOfWeek = jan1.getDay();
            
            const firstMonday = new Date(jan1);
            const daysToFirstMonday = jan1DayOfWeek === 0 ? 1 : 8 - jan1DayOfWeek;
            firstMonday.setDate(jan1.getDate() + daysToFirstMonday);
            
            dateFrom = new Date(firstMonday);
            dateFrom.setDate(firstMonday.getDate() + (weekNum - 1) * 7);
            
            dateTo = new Date(dateFrom);
            dateTo.setDate(dateFrom.getDate() + 13);
          }
        } else if (label.includes('-')) {
          // Try to parse as date range like "2024-01-01 to 2024-01-14"
          const parts = label.split(' to ');
          if (parts.length === 2) {
            dateFrom = new Date(parts[0]);
            dateTo = new Date(parts[1]);
            
            if (isNaN(dateFrom.getTime()) || isNaN(dateTo.getTime())) {
              console.error('Invalid date range format:', label);
              // Fallback
              const now = new Date();
              dateFrom = new Date(now);
              dateTo = new Date(now);
              dateTo.setDate(now.getDate() + 13);
            }
          } else {
            // Try to parse as single date
            const baseDate = new Date(label);
            if (isNaN(baseDate.getTime())) {
              console.error('Invalid biweekly date format:', label);
              // Fallback
              const now = new Date();
              dateFrom = new Date(now);
              dateTo = new Date(now);
              dateTo.setDate(now.getDate() + 13);
            } else {
              dateFrom = new Date(baseDate);
              dateTo = new Date(baseDate);
              dateTo.setDate(baseDate.getDate() + 13);
            }
          }
        } else {
          // Try to parse as date string
          const baseDate = new Date(label);
          if (isNaN(baseDate.getTime())) {
            console.error('Invalid biweekly format:', label);
            // Fallback to current biweekly period
            const now = new Date();
            dateFrom = new Date(now);
            dateTo = new Date(now);
            dateTo.setDate(now.getDate() + 13);
          } else {
            dateFrom = new Date(baseDate);
            dateTo = new Date(baseDate);
            dateTo.setDate(baseDate.getDate() + 13);
          }
        }
        break;
      
      case 'monthly':
        // For monthly periods
        if (label.includes('-')) {
          const [year, month] = label.split('-');
          const monthNum = parseInt(month);
          
          if (isNaN(monthNum) || isNaN(parseInt(year))) {
            console.error('Invalid monthly format:', label);
            // Fallback to current month
            const now = new Date();
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
            dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          } else {
            dateFrom = new Date(year, monthNum - 1, 1);
            dateTo = new Date(year, monthNum, 0);
          }
        } else if (label.includes(' ')) {
          const parts = label.split(' ');
          const monthName = parts[0];
          const year = parts[1];
          
          const monthNames = [
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'
          ];
          const monthIndex = monthNames.findIndex(name => 
            name.toLowerCase().startsWith(monthName.toLowerCase())
          );
          
          if (monthIndex !== -1 && !isNaN(parseInt(year))) {
            dateFrom = new Date(year, monthIndex, 1);
            dateTo = new Date(year, monthIndex + 1, 0);
          } else {
            console.error('Invalid monthly format:', label);
            const now = new Date();
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
            dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }
        } else {
          const monthNames = [
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'
          ];
          const monthIndex = monthNames.findIndex(name => 
            name.toLowerCase().startsWith(label.toLowerCase())
          );
          
          if (monthIndex !== -1) {
            const now = new Date();
            dateFrom = new Date(now.getFullYear(), monthIndex, 1);
            dateTo = new Date(now.getFullYear(), monthIndex + 1, 0);
          } else {
            console.error('Invalid monthly format:', label);
            const now = new Date();
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
            dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }
        }
        break;
      
      case 'quarterly':
        if (label.includes('Q')) {
          let quarter, year;
          
          if (label.includes('-')) {
            [year, quarter] = label.split('-Q');
          } else {
            const parts = label.split(' ');
            quarter = parts[0].replace('Q', '');
            year = parts[1];
          }
          
          const quarterNum = parseInt(quarter);
          
          if (isNaN(quarterNum) || isNaN(parseInt(year))) {
            console.error('Invalid quarterly format:', label);
            const now = new Date();
            const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
            const quarterStartMonth = (currentQuarter - 1) * 3;
            dateFrom = new Date(now.getFullYear(), quarterStartMonth, 1);
            dateTo = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
          } else {
            const quarterStartMonth = (quarterNum - 1) * 3;
            dateFrom = new Date(year, quarterStartMonth, 1);
            dateTo = new Date(year, quarterStartMonth + 3, 0);
          }
        } else {
          console.error('Invalid quarterly format:', label);
          const now = new Date();
          const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
          const quarterStartMonth = (currentQuarter - 1) * 3;
          dateFrom = new Date(now.getFullYear(), quarterStartMonth, 1);
          dateTo = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
        }
        break;
      
      default:
        console.error('Unknown time range:', timeRange);
        const now = new Date();
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
        dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }

    // Format dates as YYYY-MM-DD without timezone issues
    const formatDate = (date) => {
      if (isNaN(date.getTime())) {
        console.error('Invalid date object:', date);
        return new Date().toISOString().split('T')[0]; // Fallback to today
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const result = {
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo)
    };
    
    console.log('Date range result:', result); // Debug log
    return result;
  };

  const handleVisibilityTrendClick = (event, elements) => {
    if (elements.length > 0) {
      const element = elements[0];
      const dataIndex = element.index;
      
      // Get the time period label from the clicked bar
      const chart = buildVisibilityTrendDataset(visibilityTrends);
      const timePeriod = chart.labels[dataIndex];
      
      // Convert time period to date range
      const { dateFrom, dateTo } = convertTimePeriodToDateRange(timePeriod, timeRange);
      
      // Get current SMD filter
      const smd = ls.get("smd");
      const smdId = smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : null;
      
      // Build navigation parameters
      const params = new URLSearchParams();
      params.append('startDate', dateFrom);
      params.append('endDate', dateTo);
      if (smdId) {
        params.append('salesID', smdId);
      }
      
      // Navigate to visitations page with filters
      navigate(`/visitations?${params.toString()}`);
      // console.log(params.toString())
    }
  };

  return (
    <div className="store-analytics">
      <SalesPicker onFilterChange={fetchData} />
      <StockLatest />
      <CRow>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="primary"
            value={summary.data.summary.totalStores}
            title="Total Toko"
            icon={<CIcon icon={cibEventStore} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="info"
            value={
              summary.data.distribution
                .filter((s) => s.type === "A")
                .map((s) => s.count) || 0
            }
            title="Toko Tipe A"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="warning"
            value={summary.data.summary.visitedStores}
            title="Toko Terkunjungi"
            icon={<CIcon icon={cilMap} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="danger"
            value={summary.data.summary.notVisitedStores}
            title="Toko Belum Terkunjungi"
            icon={<CIcon icon={cilChartPie} height={36} />}
          />
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Stock per Store (Monthly)</CCol>
                <CCol className="text-end">
                  <small className="text-muted">Last 6 months</small>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {chart.datasets.length ? (
                <CChartBar
                  data={{ labels: chart.labels, datasets: chart.datasets }}
                  options={{
                    responsive: true,
                    plugins: { 
                      legend: { position: "bottom" },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                      }
                    },
                    interaction: { mode: "index", intersect: false },
                    scales: {
                      y: { 
                        beginAtZero: true, 
                        ticks: { precision: 0 },
                        title: {
                          display: true,
                          text: "Number of Stores"
                        }
                      },
                      x: { 
                        title: {
                          display: true,
                          text: "Months"
                        }
                      }
                    },
                    onClick: handleBarChartClick, // Add this line
                  }}
                  style={{ minHeight: 300 }}
                />
              ) : (
                <div className="text-muted">Tidak ada data stok bulanan</div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        {/* Visibility Trend */}
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Tren Visibility</CCol>
                <CCol className="text-end">
                  <CFormSelect
                    size="sm"
                    style={{ width: "auto", display: "inline-block" }}
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                  >
                    <option value="weekly">7 Hari</option>
                    <option value="biweekly">14 Hari</option>
                    <option value="monthly">30 Hari</option>
                    <option value="quarterly">90 Hari</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {(() => {
                const chart = buildVisibilityTrendDataset(visibilityTrends);
                return chart.labels.length ? (
                  <CChartBar
                    data={{ labels: chart.labels, datasets: chart.datasets }}
                    options={{
                      responsive: true,
                      plugins: { 
                        legend: { position: "bottom" },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                        }
                      },
                      interaction: { mode: "index", intersect: false },
                      scales: {
                        y: { 
                          beginAtZero: true, 
                          ticks: { precision: 0 },
                          title: {
                            display: true,
                            text: "Visit Count"
                          }
                        },
                        x: { 
                          title: {
                            display: true,
                            text: "Time Period"
                          }
                        }
                      },
                      onClick: handleVisibilityTrendClick, // Add this line
                    }}
                    style={{ minHeight: 260 }}
                  />
                ) : (
                  <div className="text-muted">Tidak ada data tren</div>
                );
              })()}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan tipe</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.distribution.map(
                    (item) => `Type ${item.type}`
                  ),
                  datasets: [
                    {
                      backgroundColor: [
                        "#4BC0C0",
                        "#36A2EB",
                        "#FFCE56",
                        "#FF6384",
                      ],
                      data: summary.data.distribution.map((item) => item.count),
                    },
                  ],
                }}
                options={{
                  onClick: handleTypeDistributionClick,
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan visibility</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.visibility.map(
                    (item) => ` ${item.visibility}`
                  ),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56"],
                      data: summary.data.visibility.map(
                        (item) => item.store_count
                      ),
                    },
                  ],
                }}
                options={{
                  onClick: handleVisibilityDistributionClick,
                }}
              />
              <CFormLabel>*Berdasarkan data kunjungan terakhir</CFormLabel>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan produk</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.product.map(
                    (item) => ` ${item.product_group}`
                  ),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384"],
                      data: summary.data.product.map(
                        (item) => item.store_count
                      ),
                    },
                  ],
                }}
                options={{
                  onClick: handleProductDistributionClick,
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>Sebaran Toko Berdasarkan Lokasi</CCardHeader>
            <CCardBody>
              <MapContainer
                center={center}
                zoom={13}
                style={{ height: "600px", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {mapDistribution.map((store) => (
                  <Marker
                    key={store.id}
                    position={
                      isNaN(store.latitude)
                        ? center
                        : [store.latitude, store.longitude]
                    }
                  >
                    <Popup>
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/stores/details/${store.id}`)}
                      >
                        <h3>{store.name}</h3>
                        <p>{store.address}</p>
                        <p
                          style={{
                            color: "#0066cc",
                            textDecoration: "underline",
                            marginTop: "8px",
                          }}
                        >
                          Lihat Detail →
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>Geographic Distribution</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: geoDistribution.map((item) => item.province),
                  datasets: [
                    {
                      label: "Stores per Province",
                      backgroundColor: "#f87979",
                      data: geoDistribution.map((item) => item.store_count),
                    },
                  ],
                }}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "right",
                    },
                  },
                  onClick: (evt, elements) => {
                    if (elements && elements.length > 0) {
                      const idx = elements[0].index;
                      handleBarClick(idx);
                    }
                  },
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard>
            <CCardHeader>Stores Needing Attention</CCardHeader>
            <CCardBody style={{ maxHeight: "400px", overflowY: "auto" }}>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Last Visit</CTableHeaderCell>
                    <CTableHeaderCell>Days Since Visit</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {salesPerformance.map((store) => (
                    <CTableRow
                      key={store.id}
                      onClick={() => navigate(`/stores/details/${store.id}`)}
                    >
                      <CTableDataCell>{store.store_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge
                          color={store.type === "A" ? "primary" : "secondary"}
                        >
                          Type {store.type}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        {store.last_visited
                          ? new Date(store.last_visited).toLocaleDateString()
                          : "Never"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge
                          color={
                            store.days_since_last_visit > 30
                              ? "danger"
                              : "warning"
                          }
                        >
                          {store.days_since_last_visit || "N/A"}
                        </CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      
      {/* Add the stores modal with DataTable */}
      <CModal 
        visible={showStoresModal} 
        onClose={() => setShowStoresModal(false)}
        size="xl"
      >
        <CModalHeader>
          <CModalTitle>{modalTitle}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {/* Category Selection */}
          <CRow className="mb-3">
            <CCol xs={12} md={6}>
              <CFormLabel>Select Category:</CFormLabel>
              <CFormSelect
                value={selectedCategory}
                onChange={handleCategoryChange}
                size="sm"
              >
                <option value="Below 50%">Below 50%</option>
                <option value="50-75%">50-75%</option>
                <option value="Above 75%">Above 75%</option>
              </CFormSelect>
            </CCol>
            <CCol xs={12} md={6}>
              <CFormLabel>Month:</CFormLabel>
              <CFormInput
                value={selectedMonth}
                readOnly
                size="sm"
                className="bg-light"
              />
            </CCol>
          </CRow>

          {/* DataTable */}
          {storeRows.length > 0 ? (
            <DataTable
              columns={storeColumns}
              rows={storeRows}
              loading={false}
              autoHeight
              density="compact"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              onRowClick={handleStoreRowClick}
              pageSizeOptions={[5, 10, 20, 25]}
            />
          ) : (
            <div className="text-center text-muted">
              <CBadge color="secondary">No stores found for this category</CBadge>
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowStoresModal(false)}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default StoreAnalytics;
